import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown, Loader2, Settings2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: '📝' },
  { value: 'textarea', label: 'Long Text / Paragraph', icon: '📄' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'phone', label: 'Phone Number', icon: '📞' },
  { value: 'url', label: 'URL / Website', icon: '🔗' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'select', label: 'Dropdown (Single Select)', icon: '📋' },
  { value: 'multiselect', label: 'Multiple Select', icon: '☑️' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'file', label: 'File Upload', icon: '📎' },
];

// Special compound field types (for startup form)
const SPECIAL_FIELD_TYPES = [
  { value: 'founders', label: 'Founders (Compound)', icon: '👥' },
  { value: 'previous_fundraises', label: 'Previous Fundraises (Compound)', icon: '💰' },
];

const ALL_FIELD_TYPES = [...FIELD_TYPES, ...SPECIAL_FIELD_TYPES];

export default function FormBuilder({ formType, formTitle }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    label: '',
    field_key: '',
    type: 'text',
    required: true,
    placeholder: '',
    section: 'General',
    options: []
  });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    fetchFormConfig();
  }, [formType]);

  const fetchFormConfig = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('user_email');
      const response = await axios.get(`${API}/admin/form-configurations/${formType}?requester_email=${userEmail}`);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching form config:', error);
      toast.error('Failed to load form configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (fieldsToSave = null) => {
    try {
      setSaving(true);
      const userEmail = localStorage.getItem('user_email');
      await axios.put(`${API}/admin/form-configurations/${formType}?requester_email=${userEmail}`, {
        fields: fieldsToSave || fields
      });
      toast.success('Form configuration saved!');
    } catch (error) {
      console.error('Error saving form config:', error);
      toast.error('Failed to save form configuration');
    } finally {
      setSaving(false);
    }
  };

  const generateFieldKey = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30);
  };

  const handleAddField = async () => {
    if (!newField.label.trim()) {
      toast.error('Field label is required');
      return;
    }

    const fieldKey = newField.field_key || generateFieldKey(newField.label);
    
    // Check for duplicate field key
    if (fields.some(f => f.field_key === fieldKey)) {
      toast.error('A field with this key already exists');
      return;
    }

    const field = {
      id: `custom_${Date.now()}`,
      field_key: fieldKey,
      label: newField.label,
      type: newField.type,
      required: newField.required,
      placeholder: newField.placeholder,
      section: newField.section || 'Custom Fields',
      is_system_field: false,
      order: fields.length + 1,
      options: newField.options || []
    };

    const updatedFields = [...fields, field];
    setFields(updatedFields);
    setShowAddDialog(false);
    resetNewField();
    
    // Auto-save after adding field
    await handleSave(updatedFields);
  };

  const handleUpdateField = async () => {
    if (!editingField) return;

    const updatedFields = fields.map(f => 
      f.id === editingField.id ? { ...editingField } : f
    );
    setFields(updatedFields);
    setEditingField(null);
    
    // Auto-save after updating field
    await handleSave(updatedFields);
  };

  const handleDeleteField = async (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    if (field?.is_system_field) {
      toast.error('Cannot delete system fields. You can only hide them by marking as non-required.');
      return;
    }

    const updatedFields = fields.filter(f => f.id !== fieldId);
    setFields(updatedFields);
    
    // Auto-save after deleting field
    await handleSave(updatedFields);
  };

  const handleMoveField = async (index, direction) => {
    const newFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    
    // Update order values
    newFields.forEach((field, idx) => {
      field.order = idx + 1;
    });
    
    setFields(newFields);
    
    // Auto-save after reordering
    await handleSave(newFields);
  };

  const handleToggleRequired = async (fieldId) => {
    const updatedFields = fields.map(f => 
      f.id === fieldId ? { ...f, required: !f.required } : f
    );
    setFields(updatedFields);
    
    // Auto-save after toggling required
    await handleSave(updatedFields);
  };

  const resetNewField = () => {
    setNewField({
      label: '',
      field_key: '',
      type: 'text',
      required: true,
      placeholder: '',
      section: 'Custom Fields',
      options: []
    });
    setNewOption('');
  };

  const addOptionToNewField = () => {
    if (!newOption.trim()) return;
    setNewField({
      ...newField,
      options: [...(newField.options || []), { label: newOption.trim(), value: newOption.trim().toLowerCase().replace(/\s+/g, '_') }]
    });
    setNewOption('');
  };

  const removeOptionFromNewField = (index) => {
    setNewField({
      ...newField,
      options: newField.options.filter((_, i) => i !== index)
    });
  };

  const addOptionToEditingField = (optionText) => {
    if (!optionText.trim() || !editingField) return;
    setEditingField({
      ...editingField,
      options: [...(editingField.options || []), { label: optionText.trim(), value: optionText.trim().toLowerCase().replace(/\s+/g, '_') }]
    });
  };

  const removeOptionFromEditingField = (index) => {
    if (!editingField) return;
    setEditingField({
      ...editingField,
      options: editingField.options.filter((_, i) => i !== index)
    });
  };

  // Group fields by section
  const groupedFields = fields.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  const getFieldTypeInfo = (type) => {
    return ALL_FIELD_TYPES.find(t => t.value === type) || { label: type, icon: '📝' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
        <span className="ml-2 text-gray-500">Loading form configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-brand" />
            {formTitle} Form Builder
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Customize the fields for {formTitle.toLowerCase()} submissions. Changes will reflect across all forms.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Field</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Field Label *</Label>
                  <Input
                    value={newField.label}
                    onChange={(e) => setNewField({ ...newField, label: e.target.value, field_key: generateFieldKey(e.target.value) })}
                    placeholder="e.g., Company Size"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Field Type *</Label>
                  <Select value={newField.type} onValueChange={(value) => setNewField({ ...newField, type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <span>{type.icon}</span>
                            <span>{type.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Placeholder Text</Label>
                  <Input
                    value={newField.placeholder}
                    onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                    placeholder="Enter placeholder text"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Section</Label>
                  <Input
                    value={newField.section}
                    onChange={(e) => setNewField({ ...newField, section: e.target.value })}
                    placeholder="e.g., Basic Information"
                  />
                </div>
                
                {/* Options for select/multiselect */}
                {(newField.type === 'select' || newField.type === 'multiselect') && (
                  <div className="space-y-2">
                    <Label>Options</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        placeholder="Add option"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOptionToNewField())}
                      />
                      <Button type="button" onClick={addOptionToNewField} variant="outline" size="sm">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(newField.options || []).map((opt, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {opt.label}
                          <button onClick={() => removeOptionFromNewField(idx)} className="ml-1 text-red-500 hover:text-red-700">
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Label>Required Field</Label>
                  <Switch
                    checked={newField.required}
                    onCheckedChange={(checked) => setNewField({ ...newField, required: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowAddDialog(false); resetNewField(); }}>
                  Cancel
                </Button>
                <Button onClick={handleAddField} className="!bg-brand hover:!bg-brand-hover !text-white">
                  Add Field
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {saving && (
            <div className="flex items-center text-sm text-[#64748B]">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </div>
          )}
        </div>
      </div>

      {/* Fields List */}
      <div className="space-y-6">
        {Object.entries(groupedFields).map(([section, sectionFields]) => (
          <Card key={section} className="border-[#E2E8F0]">
            <CardHeader className="pb-3">
              <CardTitle className="text-md font-semibold text-[#0F172A]">{section}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sectionFields.sort((a, b) => a.order - b.order).map((field, index) => {
                const fieldTypeInfo = getFieldTypeInfo(field.type);
                const globalIndex = fields.findIndex(f => f.id === field.id);
                
                return (
                  <div
                    key={field.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      field.is_system_field ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {/* Drag Handle / Reorder Buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveField(globalIndex, 'up')}
                        disabled={globalIndex === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleMoveField(globalIndex, 'down')}
                        disabled={globalIndex === fields.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>

                    {/* Field Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{fieldTypeInfo.icon}</span>
                        <span className="font-medium text-[#0F172A]">{field.label}</span>
                        {field.is_system_field && (
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                            System
                          </Badge>
                        )}
                        {field.required ? (
                          <Badge className="text-xs bg-red-100 text-red-700">Required</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-gray-500">Optional</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="font-mono bg-gray-100 px-1 rounded">{field.field_key}</span>
                        <span>•</span>
                        <span>{fieldTypeInfo.label}</span>
                        {field.placeholder && (
                          <>
                            <span>•</span>
                            <span className="italic">&ldquo;{field.placeholder}&rdquo;</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={field.required}
                        onCheckedChange={() => handleToggleRequired(field.id)}
                        className="data-[state=checked]:bg-brand"
                      />
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingField({ ...field })}
                            className="text-brand border-brand"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Edit Field</DialogTitle>
                          </DialogHeader>
                          {editingField && (
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Field Label</Label>
                                <Input
                                  value={editingField.label}
                                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Placeholder</Label>
                                <Input
                                  value={editingField.placeholder || ''}
                                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Section</Label>
                                <Input
                                  value={editingField.section || ''}
                                  onChange={(e) => setEditingField({ ...editingField, section: e.target.value })}
                                />
                              </div>
                              
                              {/* Options editor for select/multiselect */}
                              {(editingField.type === 'select' || editingField.type === 'multiselect') && (
                                <div className="space-y-2">
                                  <Label>Options</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Add option"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          addOptionToEditingField(e.target.value);
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {(editingField.options || []).map((opt, idx) => (
                                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                        {opt.label}
                                        <button onClick={() => removeOptionFromEditingField(idx)} className="ml-1 text-red-500 hover:text-red-700">
                                          ×
                                        </button>
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between">
                                <Label>Required Field</Label>
                                <Switch
                                  checked={editingField.required}
                                  onCheckedChange={(checked) => setEditingField({ ...editingField, required: checked })}
                                />
                              </div>
                            </div>
                          )}
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setEditingField(null)}>
                              Cancel
                            </Button>
                            <Button onClick={handleUpdateField} disabled={saving} className="!bg-brand hover:!bg-brand-hover !text-white">
                              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                              {saving ? 'Saving...' : 'Save Field'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {!field.is_system_field && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteField(field.id)}
                          className="text-red-500 border-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {fields.length === 0 && (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="py-12 text-center">
            <div className="text-gray-400 mb-4">
              <Settings2 className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Fields Configured</h3>
            <p className="text-gray-500 mb-4">Start building your form by adding fields</p>
            <Button onClick={() => setShowAddDialog(true)} className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add First Field
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
